import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { CanonicalTag } from '@/components/CanonicalTag';
import { StructuredData } from '@/components/StructuredData';
import { useNavigate, Link } from 'react-router-dom';
import { trackEvent } from '@/components/GoogleAnalytics';
import finishLineImage from '@/assets/triathlon-finish-line.jpg';
import Footer from '@/components/Footer';
import WhySignUpModal from '@/components/WhySignUpModal';
import { ClipboardList, Settings, Clock } from 'lucide-react';

const Start = () => {
  const navigate = useNavigate();
  const [isWhySignUpOpen, setIsWhySignUpOpen] = useState(false);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Sink Bike Run - Custom Triathlon Checklist',
    description: 'A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race.',
    applicationCategory: 'LifestyleApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };

  const handleStartChecklist = () => {
    trackEvent('cta_create_checklist_clicked', { source: 'start_page_hero' });
    navigate('/#create-checklist');
  };

  return (
    <>
      <Helmet>
        <title>Create Your Triathlon Checklist | Get Started Free</title>
        <meta 
          name="description" 
          content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." 
        />
        <meta property="og:title" content="Create Your Triathlon Checklist | Get Started Free" />
        <meta 
          property="og:description" 
          content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." 
        />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Your Triathlon Checklist | Get Started Free" />
        <meta name="twitter:description" content="A simple, customizable checklist built by a triathlete — so you can stay organized, stress less, and focus on your race." />
        <meta name="twitter:image" content="https://sinkbikerun.com/og-image.jpg" />
        
        {/* Preload hero image */}
        <link rel="preload" as="image" href={finishLineImage} fetchPriority="high" />
      </Helmet>
      <CanonicalTag url="/start" />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background">
        {/* Minimal Header - Logo only */}
        <header className="absolute top-0 left-0 right-0 z-20 py-4 md:py-6 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <Link 
              to="/" 
              className="text-white font-display font-semibold tracking-tight text-base hover:text-white/80 transition-colors"
            >
              SINK BIKE RUN
            </Link>
          </div>
        </header>

        <main>
          {/* Hero Section - Blue overlay with orange headline */}
          <section className="relative min-h-[50vh] sm:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
          <img 
            src={finishLineImage}
            alt="Triathlon athletes preparing for race"
            className="absolute inset-0 w-full h-full"
            data-hero-image="true"
            style={{ objectFit: 'cover' }}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-primary/60" />
          {/* Gradient fade into next section */}
          <div className="absolute bottom-0 left-0 right-0 h-20 sm:h-28 bg-gradient-to-b from-transparent to-background" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 py-12 sm:py-8 md:py-0 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-display font-semibold text-white mb-4 sm:mb-6 tracking-tight">
              Train Hard. Pack Smart.<br />Race Confident.
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-body text-white/95 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed">
              Build a custom triathlon checklist in minutes with event-specific templates. Make sure nothing is forgotten so you can perform at your best.
            </p>
            <div className="flex flex-col items-center">
              <Button 
                onClick={handleStartChecklist}
                size="lg"
                variant="accent"
                className="w-full md:w-auto h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200"
              >
                Create Your Checklist
              </Button>
              <p className="text-sm text-white/80 mt-6">Start now without signing up. Sign up later to save and access anytime.</p>
              <button 
                onClick={() => setIsWhySignUpOpen(true)}
                className="text-sm text-white/90 hover:text-white underline underline-offset-4 mt-4 transition-colors"
              >
                Why Sign Up?
              </button>
            </div>
          </div>
        </section>

        <WhySignUpModal 
          isOpen={isWhySignUpOpen} 
          onClose={() => setIsWhySignUpOpen(false)} 
        />

        {/* Benefits Section - White background with accent colors */}
        <section className="py-10 md:py-14 px-6 bg-background">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center mb-8 sm:mb-12 text-primary">How Sink Bike Run Helps You</h2>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <ClipboardList className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-primary mb-4">
                  Stay organized for any race
                </h3>
                <p className="text-base font-body text-muted-foreground leading-[1.65]">
                  Pre-filled essentials for swim, bike, run, and nutrition.
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Settings className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-primary mb-4">
                  Make it yours
                </h3>
                <p className="text-base font-body text-muted-foreground leading-[1.65]">
                  Customize gear and nutrition to fit your goals.
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Clock className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-primary mb-4">
                  Save time, perfect your process
                </h3>
                <p className="text-base font-body text-muted-foreground leading-[1.65]">
                  Build in minutes. Reuse and refine for every race.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Credibility Section - Light muted background */}
        <section className="py-10 md:py-14 px-6 bg-muted/30">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-primary mb-8">
              Built by an athlete, for athletes.
            </h2>
            <p className="text-lg font-body text-muted-foreground leading-[1.7]">
              Created by a triathlete who knows that being fully prepared is the only way to race your best. This tool is free, simple, and built with one goal: help you show up to the starting line prepared and confident.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-10 md:py-14 px-6 bg-background">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-primary mb-4">
              Forget nothing. Race your best.
            </h2>
            <p className="text-base font-body text-muted-foreground mb-8 leading-[1.7]">
              Start building your checklist now and make race day stress-free. It only takes a couple of minutes.
            </p>
            <div className="flex flex-col items-center gap-4">
              <Button 
                onClick={handleStartChecklist}
                size="lg"
                variant="accent"
                className="w-full md:w-auto h-12 md:h-14 px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200"
              >
                Create Your Checklist
              </Button>
              <Link 
                to="/#sample-checklists" 
                className="text-sm text-muted-foreground hover:text-accent underline underline-offset-4 transition-colors"
              >
                See a Sample Checklist
              </Link>
            </div>
          </div>
        </section>

        <Footer />
        </main>
      </div>
    </>
  );
};

export default Start;
